import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, dateiLaden, fehlertext, type SessionUser } from "./api";
import { alsDatumUndZeit as datumUndZeit, alsUhrzeit as uhrzeit, sprache } from "./datum";
import { Kundenauswertung } from "./kundenauswertung";
import { objektAuswahl } from "./objektname";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Leer, Seitenkopf } from "./ui";

/**
 * Das Arbeitszeitregister im Buero.
 *
 * Diese Seite liest und korrigiert, sie stempelt nicht. Gestempelt wird auf
 * Mein Tag, und zwar von allen, auch von der Geschaeftsleitung. Solange es hier
 * eine zweite Stempelflaeche gab, gab es zwei Masken fuer denselben Vorgang,
 * die sich nach dem Umbau auf Arbeitstag und Einsatz nicht mehr einig waren.
 *
 * Was hier bleibt: die Liste eines Zeitraums, wahlweise ueber alle Mitarbeiter,
 * mit Korrektur, Storno und Nachtrag. Dazu die beiden Wege nach draussen, der
 * Auszug fuer die Gestoria und das Register fuer eine Kontrolle.
 *
 * Ein Einsatz steht eingerueckt unter seinem Arbeitstag. Die Stunden eines
 * Einsatzes zaehlen nicht zur Summe, sie liegen bereits im Tag.
 */

interface Korrektur {
  am: string;
  von: string | null;
  grund: string | null;
  vorherBeginn: string | null;
  vorherEnde: string | null;
}

interface Storno {
  am: string;
  von: string | null;
  grund: string | null;
}

interface Abstand {
  start: number | null;
  ende: number | null;
  startOhnePosition: boolean;
  endeOhnePosition: boolean;
}

interface Zeit {
  id: string;
  art: "day" | "assignment";
  tagId: string | null;
  terminId: string | null;
  geplant: boolean | null;
  benutzerId: string;
  mitarbeiter: string;
  beginn: string;
  ende: string | null;
  objektId: string | null;
  objekt: string | null;
  quelle: string;
  empfangen: string | null;
  notiz: string | null;
  pausenSekunden: number;
  pauseOffen: boolean;
  sekunden: number;
  kunde: string | null;
  abstand: Abstand;
  storniert: Storno | null;
  korrektur: Korrektur | null;
}

interface Liste {
  von: string;
  bis: string;
  maxAbstandM: number;
  zeiten: Zeit[];
  summeSekunden: number;
}

interface Objekt {
  id: string;
  name: string;
  customer_name: string;
}

interface Person {
  id: string;
  display_name: string;
}

export function ZeitenSeite({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const istAdmin = user.role === "admin";

  const [liste, setListe] = useState<Liste | null>(null);
  const [objekte, setObjekte] = useState<Objekt[]>([]);
  const [personen, setPersonen] = useState<Person[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedtDatei, setLaedtDatei] = useState<string | null>(null);
  const [monat, setMonat] = useState(() => new Date().toISOString().slice(0, 7));
  const [wer, setWer] = useState("");
  const [bearbeitet, setBearbeitet] = useState<Zeit | null>(null);
  const [storno, setStorno] = useState<Zeit | null>(null);
  const [nachtrag, setNachtrag] = useState(false);
  /*
   * Zwei Blicke auf dieselben Stunden. Nach Mitarbeiter fragt die
   * Lohnabrechnung, nach Kunde fragt die Rechnung. Bewusst hier und nicht als
   * eigener Reiter: die Leiste oben ist voll, und beide Antworten kommen aus
   * denselben Buchungen.
   */
  const [sicht, setSicht] = useState<"mitarbeiter" | "kunden">("mitarbeiter");

  const zeitraum = useMemo(() => spanne(monat), [monat]);

  const listeLaden = useCallback(async () => {
    const p = new URLSearchParams({ von: zeitraum.von, bis: zeitraum.bis });
    if (istAdmin && wer) p.set("benutzerId", wer);
    setListe(await api.get<Liste>(`/zeiten?${p.toString()}`));
  }, [zeitraum.von, zeitraum.bis, istAdmin, wer]);

  useEffect(() => {
    void listeLaden().catch(() => setListe(null));
  }, [listeLaden]);

  useEffect(() => {
    void api.get<{ properties: Objekt[] }>("/properties")
      .then((a) => setObjekte(a.properties))
      .catch(() => setObjekte([]));
    if (istAdmin) {
      void api.get<{ users: Person[] }>("/auth/users")
        .then((a) => setPersonen(a.users))
        .catch(() => setPersonen([]));
    }
  }, [istAdmin]);

  /**
   * Auszug und Register herunterladen.
   *
   * Beide Dateien entstehen aus dem gewaehlten Monat und, beim Register, aus
   * dem gewaehlten Mitarbeiter. Steht die Auswahl auf allen, kommt das Register
   * fuer alle, ein Mitarbeiter je Seite. Das ist genau die Mappe, die man
   * einmal im Monat ausdruckt und unterschreiben laesst.
   */
  async function datei(art: "auszug" | "register") {
    setLaedtDatei(art);
    setFehler(null);
    try {
      const p = new URLSearchParams({ monat });
      if (art === "register" && wer) p.set("benutzerId", wer);
      await dateiLaden(
        `/zeiten/${art}?${p.toString()}`,
        art === "auszug" ? `Arbeitszeit-${monat}.xlsx` : `Arbeitszeitregister-${monat}.pdf`
      );
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.laden")));
    } finally {
      setLaedtDatei(null);
    }
  }

  return (
    <>
      <Seitenkopf
        titel={t("zeit.titel")}
        hinweis={t("zeit.hinweis")}
        aktionen={istAdmin && (
          <div className="flex flex-wrap gap-2">
            <Knopf art="neben" onClick={() => setNachtrag(true)}>{t("zeit.nachtragen")}</Knopf>
            <Knopf
              art="neben" disabled={laedtDatei !== null}
              onClick={() => void datei("auszug")}
            >
              {t("zeit.auszug")}
            </Knopf>
            <Knopf disabled={laedtDatei !== null} onClick={() => void datei("register")}>
              {t("zeit.register")}
            </Knopf>
          </div>
        )}
      />
      <Fehler text={fehler} />

      {istAdmin && (
        <div className="mt-4 inline-flex overflow-hidden rounded border border-linie/40">
          {(["mitarbeiter", "kunden"] as const).map((x) => (
            <button
              key={x}
              onClick={() => setSicht(x)}
              className={`min-h-11 px-4 text-sm font-semibold transition ${
                sicht === x ? "bg-marke text-white" : "bg-white text-tinte hover:bg-grund"
              }`}
            >
              {x === "mitarbeiter" ? t("auswertung.nachMitarbeiter") : t("auswertung.nachKunden")}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Feld label={t("zeit.monat")}>
          <Eingabe type="month" value={monat} onChange={(e) => setMonat(e.target.value)} />
        </Feld>
        {istAdmin && sicht === "mitarbeiter" && (
          <Feld label={t("zeit.mitarbeiter")}>
            <Auswahl value={wer} onChange={(e) => setWer(e.target.value)}>
              <option value="">{t("zeit.alle")}</option>
              {personen.map((p) => (
                <option key={p.id} value={p.id}>{p.display_name}</option>
              ))}
            </Auswahl>
          </Feld>
        )}
        {liste && sicht === "mitarbeiter" && (
          <p className="pb-2 text-sm">
            <span className="text-leise">{t("zeit.summe")}: </span>
            <span className="text-lg font-bold">{dauertext(liste.summeSekunden)}</span>
          </p>
        )}
      </div>

      {sicht === "kunden" ? (
        <Kundenauswertung monat={monat} />
      ) : !liste ? <Laedt /> : liste.zeiten.length === 0 ? (
        <Leer text={t("zeit.leer")} />
      ) : (
        <>
        <Ortsuebersicht zeiten={liste.zeiten} maxAbstandM={liste.maxAbstandM} />
        <ul className="mt-3 space-y-2">
          {liste.zeiten.map((z) => (
            <Zeile
              key={z.id}
              zeit={z}
              maxAbstandM={liste.maxAbstandM}
              zeigeName={istAdmin && !wer}
              onKorrigieren={istAdmin && !z.storniert ? () => setBearbeitet(z) : undefined}
              onStornieren={istAdmin && !z.storniert ? () => setStorno(z) : undefined}
            />
          ))}
        </ul>
        </>
      )}

      {bearbeitet && (
        <KorrekturDialog
          zeit={bearbeitet}
          objekte={objekte}
          onClose={() => setBearbeitet(null)}
          onFertig={() => { setBearbeitet(null); void listeLaden(); }}
        />
      )}
      {storno && (
        <StornoDialog
          zeit={storno}
          onClose={() => setStorno(null)}
          onFertig={() => { setStorno(null); void listeLaden(); }}
        />
      )}
      {nachtrag && (
        <NachtragDialog
          personen={personen}
          objekte={objekte}
          onClose={() => setNachtrag(false)}
          onFertig={() => { setNachtrag(false); void listeLaden(); }}
        />
      )}
    </>
  );
}


/**
 * Wo im Monat gestempelt wurde, auf einen Blick.
 *
 * Die Frage im Buero ist nicht "wie viele Meter", sondern "war jemand
 * ueberhaupt am Objekt". Deshalb drei Zahlen je Mitarbeiter statt einer
 * Karte: am Objekt, zu weit weg, ohne Position. Gezaehlt wird die Ankunft,
 * nicht das Gehen; wer richtig ankommt und beim Ausstempeln keinen Empfang
 * hat, soll nicht als auffaellig erscheinen.
 *
 * Gerechnet wird aus der Liste, die ohnehin geladen ist. Ein eigener
 * Endpunkt waere eine zweite Stelle, an der dieselbe Regel steht, und damit
 * die Gelegenheit, dass Liste und Uebersicht auseinanderlaufen.
 */
function Ortsuebersicht({ zeiten, maxAbstandM }: { zeiten: Zeit[]; maxAbstandM: number }) {
  const { t } = useTranslation();

  const einsaetze = zeiten.filter((z) => z.art === "assignment" && !z.storniert);
  if (einsaetze.length === 0) return null;

  const jeMitarbeiter = new Map<string, { name: string; am: number; weit: number; ohne: number }>();
  for (const z of einsaetze) {
    const zeile = jeMitarbeiter.get(z.benutzerId)
      ?? { name: z.mitarbeiter, am: 0, weit: 0, ohne: 0 };
    if (z.abstand.startOhnePosition || z.abstand.start === null) zeile.ohne += 1;
    else if (z.abstand.start > maxAbstandM) zeile.weit += 1;
    else zeile.am += 1;
    jeMitarbeiter.set(z.benutzerId, zeile);
  }
  const zeilen = [...jeMitarbeiter.values()].sort((a, b) => b.weit - a.weit || a.name.localeCompare(b.name));

  return (
    <Karte className="mt-4">
      <h2 className="font-bold">{t("zeit.orte.titel")}</h2>
      <p className="mb-3 text-sm text-leise">{t("zeit.orte.hinweis", { meter: maxAbstandM })}</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[28rem] text-sm">
          <thead>
            <tr className="border-b border-linie text-left text-xs uppercase tracking-wide text-leise">
              <th className="py-2">{t("zeit.mitarbeiter")}</th>
              <th className="py-2 text-right">{t("zeit.orte.amObjekt")}</th>
              <th className="py-2 text-right">{t("zeit.orte.zuWeit")}</th>
              <th className="py-2 text-right">{t("zeit.orte.ohnePosition")}</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.name} className="border-b border-linie last:border-0">
                <td className="py-2 font-medium">{z.name}</td>
                <td className="py-2 text-right">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-green-600" aria-hidden="true" />
                    {z.am}
                  </span>
                </td>
                <td className="py-2 text-right">
                  <span className={`inline-flex items-center gap-2 ${z.weit ? "font-semibold text-red-700" : ""}`}>
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${z.weit ? "bg-red-600" : "border border-linie/60"}`}
                      aria-hidden="true"
                    />
                    {z.weit}
                  </span>
                </td>
                <td className="py-2 text-right text-leise">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-leise/50" aria-hidden="true" />
                    {z.ohne}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Karte>
  );
}

function Zeile({
  zeit, maxAbstandM, zeigeName, onKorrigieren, onStornieren
}: {
  zeit: Zeit; maxAbstandM: number; zeigeName: boolean;
  onKorrigieren?: () => void; onStornieren?: () => void;
}) {
  const { t } = useTranslation();
  /*
   * Eine stornierte Zeile wird blass und durchgestrichen, verschwindet aber
   * nicht. Wer das Register liest, soll sehen, dass hier etwas war und was
   * damit passiert ist.
   */
  const aus = Boolean(zeit.storniert);
  // Einsaetze liegen im Arbeitstag. Die Einrueckung sagt das ohne ein Wort.
  const einsatz = zeit.art === "assignment";
  return (
    <li className={`rounded-card bg-white p-4 ${aus ? "opacity-60" : ""} ${einsatz ? "ms-6" : ""}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-semibold">{datum(zeit.beginn)}</span>
          <span className={`tabular-nums ${aus ? "line-through" : ""}`}>
            {uhrzeit(zeit.beginn)} bis {zeit.ende ? uhrzeit(zeit.ende) : "…"}
          </span>
          {aus && (
            <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
              {t("zeit.storniert")}
            </span>
          )}
          {zeigeName && <span className="text-sm text-leise">{zeit.mitarbeiter}</span>}
          <span className="rounded bg-grund px-2 py-0.5 text-xs font-semibold">
            {t(einsatz ? "zeit.einsatz" : "zeit.arbeitstag")}
          </span>
          {zeit.objekt && (
            <span className="text-sm text-leise">
              {zeit.kunde ? `${zeit.kunde} · ${zeit.objekt}` : zeit.objekt}
            </span>
          )}
          {zeit.geplant === false && (
            <span className="rounded bg-marke/30 px-2 py-0.5 text-xs font-semibold">
              {t("zeit.ungeplant")}
            </span>
          )}
          {zeit.quelle === "manual" && (
            <span className="rounded bg-grund px-2 py-0.5 text-xs font-semibold">
              {t("zeit.manuell")}
            </span>
          )}
          {/*
            Verspaetet uebertragen. Der Zeitpunkt stammt vom Geraet, nicht vom
            Server. Das gehoert sichtbar gemacht: bei einer Pruefung ist die
            erste Frage, woher eine Zeit kommt.
          */}
          {zeit.quelle === "app_offline" && (
            <span
              className="rounded bg-grund px-2 py-0.5 text-xs font-semibold"
              title={zeit.empfangen ? t("zeit.empfangenAm", { zeit: new Date(zeit.empfangen).toLocaleString(sprache()) }) : undefined}
            >
              {t("zeit.ohneNetz")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-lg font-bold tabular-nums ${aus ? "line-through" : ""}`}>
            {dauertext(zeit.sekunden)}
          </span>
          {onKorrigieren && (
            <Knopf art="neben" onClick={onKorrigieren}>{t("zeit.korrigieren")}</Knopf>
          )}
          {onStornieren && (
            <Knopf art="warnung" onClick={onStornieren}>{t("zeit.stornieren")}</Knopf>
          )}
        </div>
      </div>

      {einsatz && <Ortsmarke abstand={zeit.abstand} grenze={maxAbstandM} />}

      {zeit.storniert && (
        <p className="mt-2 border-t border-linie pt-2 text-sm text-red-800">
          {t("zeit.storniertAm", {
            am: datumUndZeit(zeit.storniert.am),
            von: zeit.storniert.von ?? "?"
          })}
          {zeit.storniert.grund ? `: ${zeit.storniert.grund}` : ""}
        </p>
      )}

      {zeit.pausenSekunden > 0 && (
        <p className="mt-1 text-sm text-leise">
          {t("zeit.pausenSumme", { dauer: dauertext(zeit.pausenSekunden) })}
        </p>
      )}

      {/* Die Korrekturspur gehoert sichtbar an die Zeile, nicht in ein
          Protokoll, das niemand oeffnet. Genau danach fragt eine Pruefung. */}
      {zeit.korrektur && (
        <p className="mt-2 border-t border-linie pt-2 text-sm text-leise">
          {t("zeit.korrigiertAm", {
            am: datumUndZeit(zeit.korrektur.am),
            von: zeit.korrektur.von ?? "?"
          })}
          {zeit.korrektur.grund ? `: ${zeit.korrektur.grund}` : ""}
          {zeit.korrektur.vorherBeginn && (
            <span className="ms-1">
              ({t("zeit.vorher")} {uhrzeit(zeit.korrektur.vorherBeginn)}
              {zeit.korrektur.vorherEnde ? ` bis ${uhrzeit(zeit.korrektur.vorherEnde)}` : ""})
            </span>
          )}
        </p>
      )}
    </li>
  );
}

/**
 * Was die Ortung zum Einsatz zu sagen hat.
 *
 * Drei verschiedene Aussagen, die nicht gleich aussehen duerfen:
 * eine gemessene Entfernung innerhalb der Grenze ist unauffaellig, eine
 * darueber ist rot, und "nicht messbar" ist grau. Wer "keine Position" rot
 * faerbt, hat nach einer Woche eine Liste voller roter Zeilen und schaut
 * nicht mehr hin.
 */
function Ortsmarke({ abstand, grenze }: { abstand: Abstand; grenze: number }) {
  const { t } = useTranslation();
  const stuecke: { text: string; rot: boolean }[] = [];

  for (const [wert, fehlt] of [
    [abstand.start, abstand.startOhnePosition],
    [abstand.ende, abstand.endeOhnePosition]
  ] as [number | null, boolean][]) {
    if (wert === null) {
      stuecke.push({ text: t(fehlt ? "zeit.ohnePosition" : "zeit.ohneKoordinate"), rot: false });
    } else if (wert > grenze) {
      stuecke.push({ text: t("zeit.zuWeit", { meter: wert }), rot: true });
    } else {
      stuecke.push({ text: t("zeit.abstand", { meter: wert }), rot: false });
    }
  }

  const rot = stuecke.some((s) => s.rot);
  return (
    <p className={`mt-1 text-sm ${rot ? "font-semibold text-red-700" : "text-leise"}`}>
      {stuecke.map((s) => s.text).join(" · ")}
    </p>
  );
}

function KorrekturDialog({
  zeit, objekte, onClose, onFertig
}: { zeit: Zeit; objekte: Objekt[]; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [beginn, setBeginn] = useState(() => fuerEingabe(zeit.beginn, 0));
  const [ende, setEnde] = useState(() => (zeit.ende ? fuerEingabe(zeit.ende, 0) : ""));
  const [objektId, setObjektId] = useState(zeit.objektId ?? "");
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.patch(`/zeiten/${zeit.id}`, {
        beginn: new Date(beginn).toISOString(),
        ende: ende ? new Date(ende).toISOString() : null,
        objektId: objektId || null,
        notiz: zeit.notiz,
        grund
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("zeit.korrigieren")} onClose={onClose}>
      <Fehler text={fehler} />
      <p className="mb-4 text-sm text-leise">{t("zeit.korrekturHinweis")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("zeit.beginn")}>
          <Eingabe type="datetime-local" value={beginn} onChange={(e) => setBeginn(e.target.value)} />
        </Feld>
        <Feld label={t("zeit.ende")}>
          <Eingabe type="datetime-local" value={ende} onChange={(e) => setEnde(e.target.value)} />
        </Feld>
        <Feld label={t("zeit.objektOptional")}>
          <Auswahl value={objektId} onChange={(e) => setObjektId(e.target.value)}>
            <option value="">{t("zeit.ohneObjekt")}</option>
            {objektAuswahl(objekte).map(({ objekt, beschriftung }) => (
              <option key={objekt.id} value={objekt.id}>{beschriftung}</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("zeit.grund")} hinweis={t("zeit.grundPflicht")}>
          <Eingabe value={grund} onChange={(e) => setGrund(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft || grund.trim().length < 3} onClick={() => void speichern()}>
          {t("aktion.speichern")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function StornoDialog({
  zeit, onClose, onFertig
}: { zeit: Zeit; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post(`/zeiten/${zeit.id}/storno`, { grund });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("zeit.stornieren")} onClose={onClose}>
      <Fehler text={fehler} />
      <p className="mb-4 text-sm text-leise">{t("zeit.stornoHinweis")}</p>
      <p className="mb-4 font-semibold">
        {datum(zeit.beginn)} · {uhrzeit(zeit.beginn)}
        {zeit.ende ? ` bis ${uhrzeit(zeit.ende)}` : ""} · {zeit.mitarbeiter}
      </p>
      <Feld label={t("zeit.grund")} hinweis={t("zeit.grundPflicht")}>
        <Eingabe value={grund} onChange={(e) => setGrund(e.target.value)} />
      </Feld>
      <div className="mt-5 flex gap-2">
        <Knopf art="warnung" disabled={laeuft || grund.trim().length < 3} onClick={() => void speichern()}>
          {t("zeit.stornieren")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function NachtragDialog({
  personen, objekte, onClose, onFertig
}: { personen: Person[]; objekte: Objekt[]; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [benutzerId, setBenutzerId] = useState(personen[0]?.id ?? "");
  const [beginn, setBeginn] = useState("");
  const [ende, setEnde] = useState("");
  const [objektId, setObjektId] = useState("");
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post("/zeiten", {
        benutzerId,
        beginn: new Date(beginn).toISOString(),
        ende: new Date(ende).toISOString(),
        objektId: objektId || null,
        grund
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  const vollstaendig = benutzerId && beginn && ende && grund.trim().length >= 3;

  return (
    <Dialog titel={t("zeit.nachtragen")} onClose={onClose}>
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("zeit.mitarbeiter")}>
          <Auswahl value={benutzerId} onChange={(e) => setBenutzerId(e.target.value)}>
            {personen.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("zeit.objektOptional")}>
          <Auswahl value={objektId} onChange={(e) => setObjektId(e.target.value)}>
            <option value="">{t("zeit.ohneObjekt")}</option>
            {objektAuswahl(objekte).map(({ objekt, beschriftung }) => (
              <option key={objekt.id} value={objekt.id}>{beschriftung}</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("zeit.beginn")}>
          <Eingabe type="datetime-local" value={beginn} onChange={(e) => setBeginn(e.target.value)} />
        </Feld>
        <Feld label={t("zeit.ende")}>
          <Eingabe type="datetime-local" value={ende} onChange={(e) => setEnde(e.target.value)} />
        </Feld>
        <Feld label={t("zeit.grund")} hinweis={t("zeit.grundPflicht")} breit>
          <Eingabe value={grund} onChange={(e) => setGrund(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft || !vollstaendig} onClick={() => void speichern()}>
          {t("aktion.speichern")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function Dialog({
  titel, children, onClose
}: { titel: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-full w-full max-w-2xl overflow-y-auto rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">{titel}</h2>
        {children}
        <button className="sr-only" onClick={onClose}>schliessen</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Kleinkram
// ---------------------------------------------------------------------

function dauertext(sekunden: number): string {
  const s = Math.max(0, Math.round(sekunden));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")} h`;
}

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString(sprache(), {
    weekday: "short", day: "2-digit", month: "2-digit"
  });
}

/**
 * Fuellt ein datetime-local Feld. Das erwartet Ortszeit ohne Zeitzone,
 * deshalb wird der Zeitzonenversatz abgezogen statt toISOString zu nehmen.
 * Ohne diese Rechnung steht im Feld im Sommer eine Zeit, die zwei Stunden
 * daneben liegt, und niemand merkt es, weil sie plausibel aussieht.
 */
function fuerEingabe(iso: string, plusStunden: number): string {
  const d = new Date(new Date(iso).getTime() + plusStunden * 3_600_000);
  const ort = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return ort.toISOString().slice(0, 16);
}

/** Der Monat als Zeitraum von erstem bis erstem. */
function spanne(monat: string): { von: string; bis: string } {
  const [j, m] = monat.split("-").map(Number);
  const naechster = new Date(Date.UTC(j, m, 1));
  return { von: `${monat}-01`, bis: naechster.toISOString().slice(0, 10) };
}
